import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '@peca/commons';
import { AutocompleteItem, BreadcrumbItem, ModalComponent } from '@peca/design-system';
import {
  MultiSelectAutocompleteItem,
  MultiSelectAutocompleteItemSource,
} from 'libs/design-system/src/lib/components/multi-select-autocomplete/multi-select-autocomplete.model';
import { ProductGroupDefaultService } from '../../../services/product-group-default.service';
import {
  AVAILABLE_SPECS_LIST,
  CreateProductGroupDefaultDto,
  ProductGroupDefaultDto,
  ProductGroupDefaultSpecDto,
  PRODUCT_GROUP_DEFAULT_SPECS,
  SpecDefinition,
  UpdateProductGroupDefaultDto,
  extractNumericPart,
  formatToPtBrNumber,
} from '../../../models/product-group-default.model';

export interface FormSpecItem {
  id: string;
  description: string;
  category: string;
  important: string;
  unit: string;
  value: string;
  overwrite: boolean;
}

@Component({
  selector: 'peca-product-group-default-form',
  templateUrl: './product-group-default-form.component.html',
  styleUrls: ['./product-group-default-form.component.scss'],
  standalone: false,
})
export class ProductGroupDefaultFormComponent implements OnInit {
  @ViewChild('conflictModal') conflictModal!: ModalComponent;

  private readonly service = inject(ProductGroupDefaultService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  mode: 'create' | 'edit' = 'create';
  id: string | null = null;
  isLoadingData = false;
  isSubmitting = false;
  isOverwriting = false;

  readonly form = new FormGroup({
    productGroup: new FormControl<MultiSelectAutocompleteItem[]>([], [Validators.required]),
  });

  productGroupSource = new MultiSelectAutocompleteItemSource();

  specs: FormSpecItem[] = [];
  selectedSpecIdToAdd = '';

  initialProductGroupId: string | null = null;
  initialSpecs: FormSpecItem[] = [];

  existingConflict: ProductGroupDefaultDto | null = null;
  pendingPayload: CreateProductGroupDefaultDto | null = null;

  get title(): string {
    return this.mode === 'create' ? 'Novo padrão de grupo de produtos' : 'Editar padrão de grupo de produtos';
  }

  get breadcrumbItems(): BreadcrumbItem[] {
    return [
      { label: 'Operações', path: '/operacoes' },
      { label: 'Padrões de grupo de produtos', path: '/operacoes/padroes-grupo-produtos' },
      { label: this.mode === 'create' ? 'Novo padrão' : 'Editar padrão' },
    ];
  }

  get availableSpecs(): SpecDefinition[] {
    const selectedIds = new Set(this.specs.map((s) => s.id));
    return AVAILABLE_SPECS_LIST.filter((s) => !selectedIds.has(s.id));
  }

  get existingSpecsList(): ProductGroupDefaultSpecDto[] {
    if (!this.existingConflict?.specifications) return [];
    return Object.values(this.existingConflict.specifications);
  }

  get pendingSpecsList(): ProductGroupDefaultSpecDto[] {
    if (!this.pendingPayload?.specifications) return [];
    return Object.values(this.pendingPayload.specifications);
  }

  get hasSelectedProductGroup(): boolean {
    const groups = this.form.getRawValue().productGroup;
    return !!groups && groups.length > 0;
  }

  get hasEmptySpecValues(): boolean {
    return this.specs.some((spec) => !spec.value || !spec.value.trim());
  }

  get hasChanges(): boolean {
    if (this.mode !== 'edit') return true;

    const currentGroupId = this.form.getRawValue().productGroup?.[0]?.id ?? null;
    if (currentGroupId !== this.initialProductGroupId) return true;

    if (this.specs.length !== this.initialSpecs.length) return true;

    const initialMap = new Map(this.initialSpecs.map((s) => [s.id, s]));
    for (const spec of this.specs) {
      const initSpec = initialMap.get(spec.id);
      if (!initSpec) return true;

      const currentVal = formatToPtBrNumber(spec.value) || spec.value.trim();
      const initVal = formatToPtBrNumber(initSpec.value) || initSpec.value.trim();
      if (currentVal !== initVal) return true;

      if (!!spec.overwrite !== !!initSpec.overwrite) return true;
    }

    return false;
  }

  get canSubmit(): boolean {
    if (this.isSubmitting) return false;
    if (!this.hasSelectedProductGroup) return false;
    if (this.hasEmptySpecValues) return false;
    if (this.mode === 'edit' && !this.hasChanges) return false;
    return true;
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.openEditPage(id);
      } else {
        this.initializeCreatePage();
      }
    });

    // Garante que o autocomplete mantenha apenas 1 item selecionado
    this.form.get('productGroup')?.valueChanges.subscribe((items) => {
      if (items && items.length > 1) {
        const lastSelected = [items[items.length - 1]];
        this.form.get('productGroup')?.setValue(lastSelected, { emitEvent: false });
      }
    });
  }

  private initializeCreatePage(): void {
    this.mode = 'create';
    this.id = null;
    this.specs = [];
    this.initialProductGroupId = null;
    this.initialSpecs = [];
    this.form.reset({ productGroup: [] });
    this.productGroupSource = new MultiSelectAutocompleteItemSource();
  }

  private openEditPage(id: string): void {
    this.mode = 'edit';
    this.id = id;
    this.isLoadingData = true;

    this.service.getById(id).subscribe({
      next: (item) => {
        this.isLoadingData = false;
        this.form.patchValue({
          productGroup: [
            {
              id: item.productGroupId,
              label: item.productGroupName,
              selected: true,
            },
          ],
        });

        this.specs = [];
        if (item.specifications) {
          for (const [specId, specDto] of Object.entries(item.specifications)) {
            const def = PRODUCT_GROUP_DEFAULT_SPECS[specId];
            if (def) {
              this.specs.push({
                id: specId,
                description: def.description,
                category: def.category,
                important: def.important,
                unit: def.unit,
                value: extractNumericPart(specDto.value),
                overwrite: !!specDto.overwrite,
              });
            }
          }
        }

        this.initialProductGroupId = item.productGroupId;
        this.initialSpecs = this.specs.map((s) => ({ ...s }));
      },
      error: () => {
        this.isLoadingData = false;
        this.toast.failure('Não foi possível carregar os dados do padrão.');
        this.navigateBack();
      },
    });
  }

  onProductGroupAutocomplete(search: string): void {
    if (search.trim().length < 3) {
      this.productGroupSource = new MultiSelectAutocompleteItemSource();
      return;
    }

    this.productGroupSource = new MultiSelectAutocompleteItemSource([], true);
    this.service.suggestProductGroups(search).subscribe({
      next: (items) => (this.productGroupSource = new MultiSelectAutocompleteItemSource(this.toMultiSelectItems(items))),
      error: () => (this.productGroupSource = new MultiSelectAutocompleteItemSource([], false, true)),
    });
  }

  addSpec(): void {
    if (!this.selectedSpecIdToAdd) return;

    const def = PRODUCT_GROUP_DEFAULT_SPECS[this.selectedSpecIdToAdd];
    if (def) {
      this.specs.push({
        id: def.id,
        description: def.description,
        category: def.category,
        important: def.important,
        unit: def.unit,
        value: '',
        overwrite: false,
      });
      this.selectedSpecIdToAdd = '';
    }
  }

  removeSpec(specId: string): void {
    this.specs = this.specs.filter((s) => s.id !== specId);
  }

  onSpecBlur(spec: FormSpecItem): void {
    if (spec.value) {
      spec.value = formatToPtBrNumber(spec.value);
    }
  }

  onSubmit(): void {
    if (!this.canSubmit) return;

    const rawGroups = this.form.getRawValue().productGroup;
    if (!rawGroups || rawGroups.length === 0) {
      this.toast.failure('Por favor, selecione um grupo de produtos.');
      return;
    }

    const selectedGroup = rawGroups[0];
    const specifications: Record<string, ProductGroupDefaultSpecDto> = {};

    for (const spec of this.specs) {
      const formattedNum = formatToPtBrNumber(spec.value) || '0,000';
      specifications[spec.id] = {
        overwrite: !!spec.overwrite,
        description: spec.description,
        category: spec.category,
        important: spec.important,
        value: `${formattedNum} ${spec.unit}`,
      };
    }

    if (this.mode === 'edit' && this.id) {
      this.handleEditSubmit(this.id, selectedGroup.id, selectedGroup.label, specifications);
    } else {
      this.handleCreateSubmit(selectedGroup.id, selectedGroup.label, specifications);
    }
  }

  private handleEditSubmit(
    currentId: string,
    productGroupId: string,
    productGroupName: string,
    specifications: Record<string, ProductGroupDefaultSpecDto>
  ): void {
    this.isSubmitting = true;

    const payload: UpdateProductGroupDefaultDto = {
      productGroupId,
      productGroupName,
      specifications,
    };

    this.service.getById(productGroupId, true).subscribe({
      next: (existingDefault) => {
        if (existingDefault.id !== currentId) {
          this.isSubmitting = false;
          this.existingConflict = existingDefault;
          this.pendingPayload = payload;
          this.conflictModal?.open();
        } else {
          this.executePut(currentId, payload);
        }
      },
      error: (err) => {
        if (err.status === 404) {
          this.executePut(currentId, payload);
        } else {
          this.isSubmitting = false;
          this.toast.failure('Erro ao verificar existência do grupo de produtos.');
        }
      },
    });
  }

  private handleCreateSubmit(
    productGroupId: string,
    productGroupName: string,
    specifications: Record<string, ProductGroupDefaultSpecDto>
  ): void {
    this.isSubmitting = true;

    // GET by id passando productGroupId para verificar se já existe
    this.service.getById(productGroupId, true).subscribe({
      next: (existingDefault) => {
        this.isSubmitting = false;
        // Já existe um default! Exibir confirmação
        this.existingConflict = existingDefault;
        this.pendingPayload = {
          productGroupId,
          productGroupName,
          specifications,
        };
        this.conflictModal?.open();
      },
      error: (err) => {
        if (err.status === 404) {
          // Não existe: fazer o POST
          this.executePost({
            productGroupId,
            productGroupName,
            specifications,
          });
        } else {
          this.isSubmitting = false;
          this.toast.failure('Erro ao verificar existência do grupo de produtos.');
        }
      },
    });
  }

  private executePost(payload: CreateProductGroupDefaultDto): void {
    this.service.create(payload).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.toast.success('Padrão de grupo de produtos cadastrado com sucesso.');
        this.navigateBack();
      },
      error: () => {
        this.isSubmitting = false;
        this.toast.failure('Erro ao cadastrar padrão de grupo de produtos.');
      },
    });
  }

  private executePut(id: string, payload: UpdateProductGroupDefaultDto): void {
    this.isSubmitting = true;
    this.service.update(id, payload).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.toast.success('Padrão de grupo de produtos atualizado com sucesso.');
        this.navigateBack();
      },
      error: () => {
        this.isSubmitting = false;
        this.toast.failure('Erro ao atualizar padrão de grupo de produtos.');
      },
    });
  }

  onCancelConflict(): void {
    this.conflictModal?.close();
    this.existingConflict = null;
    this.pendingPayload = null;
    this.isSubmitting = false;
  }

  onOverwriteConflict(): void {
    if (!this.existingConflict || !this.pendingPayload) return;

    const targetId = this.existingConflict.id;
    const currentEditId = this.mode === 'edit' && this.id && this.id !== targetId ? this.id : null;

    this.isOverwriting = true;
    this.service.update(targetId, this.pendingPayload).subscribe({
      next: () => {
        if (currentEditId) {
          this.service.delete(currentEditId).subscribe({
            next: () => this.finishOverwriteSuccess(),
            error: () => {
              this.isOverwriting = false;
              this.toast.failure('Erro ao excluir o padrão anterior após sobrescrever.');
            },
          });
        } else {
          this.finishOverwriteSuccess();
        }
      },
      error: () => {
        this.isOverwriting = false;
        this.toast.failure('Erro ao sobrescrever padrão de grupo de produtos.');
      },
    });
  }

  private finishOverwriteSuccess(): void {
    this.isOverwriting = false;
    this.conflictModal?.close();
    this.toast.success('Padrão de grupo de produtos sobrescrito com sucesso.');
    this.navigateBack();
  }

  navigateBack(): void {
    this.router.navigate(['/operacoes/padroes-grupo-produtos']);
  }

  private toMultiSelectItems(items: AutocompleteItem[]): MultiSelectAutocompleteItem[] {
    return items.map((item) => ({
      id: String(item.id),
      label: item.label,
      description: item.description,
      selected: false,
    }));
  }
}
